
import React from 'react';
import LogoGSCInline from './LogoGSCInline';
import { useGuitarMatch } from '../context/GuitarMatchContext';
import { TRIAL_LIMITS } from '../services/config';
import { UserProfile } from '../types';

interface SideNavigationProps {
  onClose: () => void;
  onOpenHub: (tab: string) => void;
  onOpenSupport: () => void;
  onOpenTeacher: () => void;
  onStartTutorial: () => void;
  onStartAssessment: () => void;
  onOpenGodMode: () => void;
}

export const SideNavigation: React.FC<SideNavigationProps> = ({ 
    onClose, onOpenHub, onOpenSupport, onOpenTeacher, onStartTutorial, onStartAssessment, onOpenGodMode
}) => {
  const { userProfile, setHubTab, setActiveOverlay, analysisCount, isPremium, setShowTutorial, setShowSubscriptionOffer } = useGuitarMatch();

  const remaining = Math.max(0, TRIAL_LIMITS.FREE_ANALYSIS_COUNT - analysisCount);

  const handleOpenInstitution = () => {
      setHubTab('INSTITUTION');
      setActiveOverlay('HUB');
      onClose();
  };

  const handleOpenSongbook = () => {
      setHubTab('SONGBOOK');
      setActiveOverlay('HUB');
      onClose();
  };

  return (
    <div className="fixed inset-0 z-[11000] flex">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity" onClick={onClose}></div>
      
      <div className="relative w-80 max-w-[85vw] h-full bg-[#00141a] border-r border-white/10 shadow-2xl flex flex-col animate-fade-in-left overflow-hidden">
        
        <div className="p-8 border-b border-white/5 bg-black/20 flex flex-col items-center">
            <button 
                onClick={() => { onOpenHub('REWARDS'); onClose(); }}
                className="w-full group flex items-center gap-4 p-4 rounded-3xl bg-white/5 border border-white/10 hover:border-smart-gold/50 transition-all shadow-xl"
            >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-smart-gold to-yellow-200 p-[1px] shrink-0">
                    <img src={(userProfile as UserProfile).avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${(userProfile as UserProfile).pseudo}`} className="w-full h-full rounded-2xl bg-black" alt="Profile" />
                </div>
                <div className="flex flex-col items-start overflow-hidden">
                    <span className="text-xs font-black uppercase text-white truncate w-full group-hover:text-smart-gold transition-colors">{(userProfile as UserProfile).pseudo}</span>
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider italic">Lvl {Math.floor((userProfile as UserProfile).stats.levelXP/1000) + 1} • {(userProfile as UserProfile).title}</span>
                </div>
            </button>

            {!isPremium ? (
                <div className="mt-6 w-full space-y-4">
                    <button 
                        onClick={() => { setShowSubscriptionOffer(true); onClose(); }}
                        className="w-full py-4 rounded-2xl bg-gradient-to-r from-smart-gold to-yellow-500 text-black font-black uppercase text-[10px] tracking-[0.2em] shadow-[0_0_30px_rgba(218,144,0,0.3)] hover:scale-105 transition-all"
                    >
                        💎 Devenir Premium
                    </button>
                    <div className="w-full bg-black/40 rounded-2xl p-4 border border-white/5">
                        <div className="flex justify-between text-[8px] font-black text-slate-500 uppercase tracking-widest mb-2">
                            <span>Essais Gratuits</span>
                            <span className="text-smart-gold">{remaining} restants</span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                            <div 
                                className="h-full bg-smart-gold transition-all duration-1000" 
                                style={{ width: `${(remaining / TRIAL_LIMITS.FREE_ANALYSIS_COUNT) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mt-6 w-full px-4 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Abonnement Master Actif</span>
                </div>
            )}
        </div>

        <nav className="flex-1 p-6 space-y-6 overflow-y-auto custom-scrollbar">
            {/* SECTION STUDIO */}
            <div className="space-y-2">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.4em] ml-2 mb-2 block">Cœur de Studio</span>
                <NavButton icon="🎓" label="Mon École" sub="Lier mon conservatoire" onClick={handleOpenInstitution} />
                <NavButton icon="🔄" label="Club Sync" sub="Tes morceaux favoris" onClick={handleOpenSongbook} />
                <NavButton icon="🚀" label="Audit Niveau" sub="Calibration IA" onClick={onStartAssessment} />
            </div>

            {/* SECTION SOCIAL & ARENA */}
            <div className="space-y-2">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.4em] ml-2 mb-2 block">Compétition</span>
                <NavButton icon="⚔️" label="Arena Mode" sub="Défis entre membres" onClick={() => onOpenHub('ARENA')} highlight={true} />
                <NavButton icon="🥇" label="League" sub="Classement hebdomadaire" onClick={() => onOpenHub('LEAGUE')} />
                <NavButton icon="🎁" label="Récompenses" sub="Galerie des trophées" onClick={() => onOpenHub('REWARDS')} />
            </div>

            {/* SECTION TECH & HELP */}
            <div className="space-y-2">
                <span className="text-[8px] font-black text-slate-500 uppercase tracking-[0.4em] ml-2 mb-2 block">Assistance & Tech</span>
                <NavButton icon="📂" label="Archives" sub="Historique des prises" onClick={() => onOpenHub('HISTORY')} />
                <NavButton icon="📖" label="Guide Studio" sub="Manuel utilisateur" onClick={() => onOpenHub('MANUAL')} />
                <NavButton icon="🎛️" label="Core Tuner" sub="Sliders de sévérité (Expert)" onClick={onOpenGodMode} highlight={true} />
                <NavButton icon="⚙️" label="Debug & Logs" sub="Moteurs Neural & DSP" onClick={() => onOpenHub('DIAG')} />
            </div>
        </nav>

        <div className="p-8 border-t border-white/5 bg-black/20 flex flex-col gap-4">
            <button 
                onClick={() => { setActiveOverlay('STEM'); onClose(); }}
                className="w-full py-4 rounded-2xl bg-white/5 border border-white/10 text-white font-black uppercase text-[10px] tracking-widest hover:bg-white hover:text-black transition-all"
            >
                Importer via URL
            </button>
            <button 
                onClick={() => { setShowTutorial(true); onClose(); }}
                className="w-full py-4 rounded-2xl bg-smart-gold text-black font-black uppercase text-[10px] tracking-widest hover:bg-white transition-all shadow-xl"
            >
                Démarrer le Tour
            </button>
        </div>
      </div>
    </div>
  );
};

const NavButton = ({ icon, label, sub, onClick, highlight = false }: { icon: string, label: string, sub: string, onClick: () => void, highlight?: boolean }) => (
    <button 
        onClick={onClick}
        className={`w-full p-4 rounded-2xl flex items-center gap-5 transition-all text-left group ${highlight ? 'bg-smart-accent/10 border border-smart-accent/20' : 'hover:bg-white/5'}`}
    >
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl border transition-colors shadow-lg ${highlight ? 'bg-smart-accent text-white border-smart-accent' : 'bg-black/40 border-white/5 group-hover:border-smart-accent'}`}>
            {icon}
        </div>
        <div>
            <div className={`text-sm font-black uppercase italic tracking-tight ${highlight ? 'text-smart-accent' : 'text-white'}`}>{label}</div>
            <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{sub}</div>
        </div>
    </button>
);
